import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);

    const checkAuth = useCallback(async () => {
        if (window.location.hash?.includes('session_id=')) {
            setLoading(false);
            return;
        }
        
        try {
            const response = await fetch(`${API}/auth/me`, {
                credentials: 'include',
            });
            
            if (response.ok) {
                const userData = await response.json();
                setUser(userData);
                setIsAuthenticated(true);
                try {
                    const adminRes = await fetch(`${API}/admin/check`, {
                        credentials: 'include',
                    });
                    if (adminRes.ok) {
                        const adminData = await adminRes.json();
                        setIsAdmin(adminData.isAdmin === true);
                    }
                } catch (adminErr) { console.warn('Admin check failed:', adminErr.message); }
            } else {
                setUser(null);
                setIsAuthenticated(false);
                setIsAdmin(false);
            }
        } catch (error) {
            setUser(null);
            setIsAuthenticated(false);
            setIsAdmin(false);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        checkAuth();
    }, [checkAuth]);

    const login = () => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + '/dashboard';
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    const logout = async () => {
        try {
            await fetch(`${API}/auth/logout`, {
                method: 'POST',
                credentials: 'include',
            });
        } catch (error) {
            console.warn('Logout error:', error.message);
        } finally {
            setUser(null);
            setIsAuthenticated(false);
            setIsAdmin(false);
            window.location.href = '/';
        }
    };

    const updateUser = useCallback((newUserData) => {
        setUser(prev => ({ ...prev, ...newUserData }));
        setIsAuthenticated(true);
    }, []);

    const value = {
        user,
        loading,
        isAuthenticated,
        isAdmin,
        login,
        logout,
        updateUser,
        checkAuth,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
